import { useMutation } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import CallTranscriptView from "components/CallTranscript/CallTranscriptView";
import { formatError } from "helpers/formatError";
import { useCallTranscript } from "hooks/useCallTranscript";
import CampaignTargetCallService from "services/CampaignTargetCall";

function DialogBody({ callId }) {
  const {
    isLoading,
    error,
    turns,
    turnsEn,
    speakers,
    speakersSwapped,
    canSwapSpeakers,
    swapSpeakers,
    translatedFrom,
    translationFailed,
    isInProgress,
    isFailed,
    isEmpty,
  } = useCallTranscript(callId);

  const { mutate: download, isPending: isDownloading } = useMutation({
    mutationFn: () => CampaignTargetCallService.downloadTranscript(callId),
    onError: err =>
      toast.error(formatError(err, "Failed to download the transcript")),
  });

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Skeleton className="tw:h-12" />
        <Skeleton className="tw:h-12" />
        <Skeleton className="tw:h-12" />
      </div>
    );
  }

  if (isInProgress) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:text-[var(--color-text-secondary)]">
        <Spinner />
        <span>Transcription in progress…</span>
      </div>
    );
  }

  if (isFailed) {
    return (
      <p className="tw:text-[var(--color-text-secondary)]">
        This recording could not be transcribed.
      </p>
    );
  }

  if (error) {
    return (
      <p className="tw:text-[var(--color-text-secondary)]">
        This call has no transcript.
      </p>
    );
  }

  if (isEmpty) {
    return (
      <p className="tw:text-[var(--color-text-secondary)]">
        No speech was detected in this recording.
      </p>
    );
  }

  return (
    <CallTranscriptView
      turns={turns}
      turnsEn={turnsEn}
      speakers={speakers}
      translatedFrom={translatedFrom}
      translationFailed={translationFailed}
      canSwapSpeakers={canSwapSpeakers}
      speakersSwapped={speakersSwapped}
      onSwapSpeakers={swapSpeakers}
      onDownload={download}
      isDownloading={isDownloading}
    />
  );
}

/**
 * Read-only transcript dialog. It owns every state the transcript can be in —
 * loading, still running, failed, empty, ready — so the menu item that opens it
 * does not have to encode transcription progress.
 */
const ViewCallTranscriptDialog = ({ callId, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="tw:sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Call Transcript</DialogTitle>
        <DialogDescription>
          Transcribed from the call recording.
        </DialogDescription>
      </DialogHeader>
      <div className="tw:max-h-[60vh] tw:overflow-y-auto">
        {open && <DialogBody callId={callId} />}
      </div>
    </DialogContent>
  </Dialog>
);

ViewCallTranscriptDialog.propTypes = {
  callId: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};

export default ViewCallTranscriptDialog;
